/* oxlint-disable promise/no-promise-in-callback */
import { Component, type ReactNode } from 'react';
import isFunction from 'lodash/isFunction';
import StackTrace from 'stacktrace-js';
import ErrorPage from '../../../pages/status-pages/components/ErrorPage';
import { isDev } from '../../../common/debug/utils';

const isLogFunctionAvailable = () => {
  const { etiJsLog } = window;
  return etiJsLog && isFunction(etiJsLog.log);
};

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (!isDev && isLogFunctionAvailable()) {
      StackTrace.fromError(error)
        .then((stack) => {
          const trace = stack[0];
          const traceParts = (trace.fileName ?? '').split('/');
          const component = traceParts.at(-1)?.split('.')[0];

          window.etiJsLog?.log(
            `[REACT] ${error.toString()}`,
            window.location.pathname,
            trace.lineNumber,
            component,
          );
        })
        .catch((err) => {
          console.error(`StackTrace Error: ${err}`); // oxlint-disable-line no-console
          window.etiJsLog?.log(
            `StackTrace failed with: ${err} while processing Error: ${error} with stack: ${error.stack}`,
            window.location.pathname,
          );
        });
    }
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;

    return hasError ? <ErrorPage /> : children;
  }
}

export default ErrorBoundary;
